"use client";

import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";

type SummaryTab = "doc" | "global" | "conversation";

// Shared markdown renderer
function MdContent({ content }: { content: string }) {
  return (
    <div className="text-sm text-gray-700 leading-relaxed">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          h1: ({ children }) => <h1 className="font-bold text-gray-900 mt-3 mb-1 text-sm">{children}</h1>,
          h2: ({ children }) => <h2 className="font-semibold text-gray-900 mt-2 mb-1 text-sm">{children}</h2>,
          h3: ({ children }) => <h3 className="font-medium text-gray-800 mt-1.5 mb-0.5 text-sm">{children}</h3>,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function SummaryLoading({ label }: { label: string }) {
  return (
    <div className="py-3">
      <div className="flex gap-1 mb-2">
        <span className="typing-dot w-2 h-2 rounded-full bg-green-400 inline-block" />
        <span className="typing-dot w-2 h-2 rounded-full bg-green-400 inline-block" />
        <span className="typing-dot w-2 h-2 rounded-full bg-green-400 inline-block" />
      </div>
      {label && <p className="text-xs text-gray-400">{label}</p>}
    </div>
  );
}

interface PDFDoc {
  doc_id: string;
  doc_title: string;
  doc_name?: string;
  page_count: number;
  chunk_count: number;
  ai_summary?: string;
  ingested_at?: string;
}

interface SelectionPopup {
  x: number;
  y: number;
  text: string;
}

interface TextWord {
  text: string;
  x0: number; y0: number;
  x1: number; y1: number;
}

interface TextLayer {
  page_width: number;
  page_height: number;
  words: TextWord[];
}

export default function PDFViewer({ onDocSelect, focusDocId }: { onDocSelect?: (docId: string, docTitle: string) => void; focusDocId?: string } = {}) {
  const [documents, setDocuments] = useState<PDFDoc[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<PDFDoc | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageUrl, setPageUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<SelectionPopup | null>(null);
  const [analysisResult, setAnalysisResult] = useState<string>("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [sessionId] = useState(() =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        })
  );
  const [textLayer, setTextLayer] = useState<TextLayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const focusAppliedRef = useRef<string | null>(null);
  const [ingesting, setIngesting] = useState(false);
  const [ingestStatus, setIngestStatus] = useState<string>("");

  // 3-tab summary state
  const [summaryTab, setSummaryTab] = useState<SummaryTab>("global");
  const [globalSummary, setGlobalSummary] = useState("");
  const [globalLoading, setGlobalLoading] = useState(false);
  const [convSummary, setConvSummary] = useState("");
  const [convLoading, setConvLoading] = useState(false);

  // Perspective summaries — keyed by doc_id, cached locally + in MongoDB
  const [perspectiveSummaries, setPerspectiveSummaries] = useState<Record<string, string>>({});
  const [perspectiveLoading, setPerspectiveLoading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`/api/pdf/list`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch {}
  };

  const loadPage = async (doc: PDFDoc, page: number) => {
    setLoading(true);
    setTextLayer(null);
    const url = `/api/pdf/page/${doc.doc_id}/${page}?dpi=150`;
    setPageUrl(url);
    setCurrentPage(page);
    // Fetch text layer alongside the image
    try {
      const res = await fetch(`/api/pdf/textlayer/${doc.doc_id}/${page}`);
      if (res.ok) {
        const data: TextLayer = await res.json();
        setTextLayer(data.words?.length ? data : null);
      }
    } catch {
      // Text layer unavailable (scanned page) — image-only mode
    }
    setLoading(false);
  };

  const loadPerspectiveSummary = async (doc: PDFDoc) => {
    // Return from local cache if already loaded (but not if it was an error)
    const cached = perspectiveSummaries[doc.doc_id];
    if (cached && !cached.startsWith("❌")) return;
    setPerspectiveLoading(true);
    try {
      const res = await fetch(`/api/pdf/summaries/perspective/${doc.doc_id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPerspectiveSummaries((prev) => ({
        ...prev,
        [doc.doc_id]: data.summary || "Ingen data tilgængelig.",
      }));
    } catch {
      setPerspectiveSummaries((prev) => ({
        ...prev,
        [doc.doc_id]: "❌ Fejl ved hentning af perspektivoversigt.",
      }));
    } finally {
      setPerspectiveLoading(false);
    }
  };

  const selectDocument = (doc: PDFDoc) => {
    setSelectedDoc(doc);
    setCurrentPage(1);
    setAnalysisResult("");
    loadPage(doc, 1);
    // Always pre-load (or serve from cache) the perspective summary for this doc
    loadPerspectiveSummary(doc);
    onDocSelect?.(doc.doc_id, doc.doc_title);
  };

  // Auto-select a document when focusDocId is provided (e.g. from "Open in Klimadokumenter")
  useEffect(() => {
    if (!focusDocId || documents.length === 0) return;
    if (focusAppliedRef.current === focusDocId) return;
    const doc = documents.find((d) => d.doc_id === focusDocId);
    if (doc) {
      focusAppliedRef.current = focusDocId;
      selectDocument(doc);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDocId, documents]);

  // Handle text selection
  const handleMouseUp = (e: React.MouseEvent) => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (text && text.length > 10) {
      setPopup({ x: e.clientX, y: e.clientY, text });
    } else {
      setPopup(null);
    }
  };

  const analyzeSelection = async (type: string) => {
    if (!popup) return;
    setAnalysisLoading(true);
    setAnalysisResult("");
    setPopup(null);

    try {
      const res = await fetch(`/api/pdf/analyze-selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selected_text: popup.text,
          doc_id: selectedDoc?.doc_id,
          analysis_type: type,
          session_id: sessionId,
          use_rag: true,
        }),
      });

      if (!res.ok) throw new Error("API error");

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      setAnalysisLoading(false);

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") break;
            try {
              const parsed = JSON.parse(data);
              if (parsed.token) {
                fullText += parsed.token.replace(/\\n/g, "\n");
                setAnalysisResult(fullText);
              }
            } catch {}
          }
        }
      }
    } catch {
      setAnalysisResult("Der opstod en fejl ved analysen.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const ingestPdfs = async () => {
    // PDF ingestion is an admin-only operation — use the Admin panel instead.
    setIngestStatus("Brug Admin-panelet til at indlæse PDF-dokumenter.");
  };

  const loadGlobalSummary = async () => {
    if (globalSummary) return; // cached
    setGlobalLoading(true);
    try {
      const res = await fetch(`/api/pdf/summaries/global`);
      const data = await res.json();
      setGlobalSummary(data.summary || "Ingen data tilgængelig.");
    } catch {
      setGlobalSummary("❌ Fejl ved hentning af global sammenfatning.");
    } finally {
      setGlobalLoading(false);
    }
  };

  const loadConvSummary = async () => {
    setConvLoading(true);
    try {
      const res = await fetch(
        `/api/pdf/summaries/conversation/${sessionId}`
      );
      const data = await res.json();
      setConvSummary(data.summary || "Ingen samtalehistorik fundet.");
    } catch {
      setConvSummary("❌ Fejl ved hentning af samtaleanalyse.");
    } finally {
      setConvLoading(false);
    }
  };

  const handleSummaryTab = (tab: SummaryTab) => {
    setSummaryTab(tab);
    if (tab === "global") {
      if (selectedDoc) {
        // Perspective mode: load per-document summary
        loadPerspectiveSummary(selectedDoc);
      } else if (!globalSummary && !globalLoading) {
        // Fallback: no doc selected → load generic global summary
        loadGlobalSummary();
      }
    }
    if (tab === "conversation") loadConvSummary(); // always refresh
  };

  const analysisTypes = [
    { type: "explain", label: "📖 Forklar" },
    { type: "summarize", label: "📝 Sammenfat" },
    { type: "action", label: "⚡ Handlinger" },
    { type: "relate", label: "🌿 Klimarelation" },
  ];

  return (
    <div className="flex-1 flex flex-col bg-gray-50">
      <div className="max-w-7xl mx-auto w-full px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">📄 Klimadokumenter</h2>
          {documents.length === 0 && (
            <button
              onClick={ingestPdfs}
              disabled={ingesting}
              className="px-4 py-2 text-white rounded-xl text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#2d6a4f" }}
            >
              {ingesting ? "⏳ Indlæser..." : "🔄 Indlæs PDF'er"}
            </button>
          )}
          {ingestStatus && (
            <span className="text-sm text-gray-600 ml-3">{ingestStatus}</span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Document list */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Dokumenter
              </h3>
              {documents.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <div className="text-3xl mb-2">📭</div>
                  <p>Ingen dokumenter endnu.</p>
                  <p className="mt-1">Klik &quot;Indlæs PDF&#39;er&quot; ovenfor.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <button
                      key={doc.doc_id}
                      onClick={() => selectDocument(doc)}
                      className={`w-full text-left p-3 rounded-lg text-sm transition-colors border ${
                        selectedDoc?.doc_id === doc.doc_id
                          ? "border-green-300 bg-green-50 text-green-800"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                    >
                      <div className="font-medium truncate">{doc.doc_title}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.page_count} sider · {doc.chunk_count} segmenter
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* PDF viewer */}
          <div className="lg:col-span-2">
            <div
              ref={containerRef}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              onMouseUp={handleMouseUp}
            >
              {!selectedDoc ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <div className="text-5xl mb-4">📄</div>
                  <p className="text-lg font-medium text-gray-600">Vælg et dokument</p>
                  <p className="text-sm mt-2">Markér tekst for AI-analyse</p>
                </div>
              ) : (
                <>
                  {/* Page navigation */}
                  <div
                    className="flex items-center justify-between px-4 py-3 text-white text-sm"
                    style={{ backgroundColor: "#1e3a5f" }}
                  >
                    <button
                      onClick={() => selectedDoc && loadPage(selectedDoc, Math.max(1, currentPage - 1))}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ← Forrige
                    </button>
                    <span>
                      Side {currentPage} / {selectedDoc.page_count}
                    </span>
                    <button
                      onClick={() => selectedDoc && loadPage(selectedDoc, Math.min(selectedDoc.page_count, currentPage + 1))}
                      disabled={currentPage >= selectedDoc.page_count}
                      className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Næste →
                    </button>
                  </div>

                  {/* PDF page — image + transparent selectable text overlay */}
                  <div className="pdf-selectable p-2 min-h-96 flex items-center justify-center">
                    {loading ? (
                      <div className="text-gray-400">⏳ Indlæser side...</div>
                    ) : pageUrl ? (
                      <div className="relative inline-block max-w-full">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={pageUrl}
                          alt={`Side ${currentPage}`}
                          className="max-w-full h-auto shadow-sm rounded block select-none"
                          draggable={false}
                        />
                        {/* Transparent text layer — enables native browser text selection */}
                        {textLayer && (
                          <div
                            className="absolute inset-0 overflow-hidden"
                            aria-hidden="false"
                            style={{ cursor: "text", userSelect: "text" }}
                          >
                            {textLayer.words.map((word, i) => (
                              <span
                                key={i}
                                style={{
                                  position: "absolute",
                                  left: `${word.x0 * 100}%`,
                                  top: `${word.y0 * 100}%`,
                                  width: `${(word.x1 - word.x0) * 100}%`,
                                  height: `${(word.y1 - word.y0) * 100}%`,
                                  color: "transparent",
                                  userSelect: "text",
                                  whiteSpace: "pre",
                                  overflow: "hidden",
                                  lineHeight: 1,
                                }}
                              >
                                {word.text}{" "}
                              </span>
                            ))}
                          </div>
                        )}
                        {!textLayer && (
                          <div className="absolute bottom-2 right-2 text-xs bg-black/40 text-white px-2 py-0.5 rounded pointer-events-none">
                            📷 Skannet side — tekst ikke valgbar
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Selection popup */}
            {popup && (
              <div
                className="fixed z-50 bg-white shadow-xl rounded-xl border border-gray-200 p-3"
                style={{ top: popup.y + 10, left: Math.min(popup.x, window.innerWidth - 280) }}
              >
                <div className="text-xs text-gray-500 mb-2 max-w-60 truncate">
                  &quot;{popup.text.slice(0, 60)}...&quot;
                </div>
                <div className="flex flex-wrap gap-1">
                  {analysisTypes.map((a) => (
                    <button
                      key={a.type}
                      onClick={() => analyzeSelection(a.type)}
                      className="text-xs px-2 py-1 rounded-lg text-white font-medium hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: "#2d6a4f" }}
                    >
                      {a.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setPopup(null)}
                    className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary panel — 3 tabs */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Tab bar */}
              <div className="flex border-b border-gray-100">
                {([
                  { id: "doc" as const, label: "Dette dokument", icon: "📄" },
                  { id: "global" as const, label: "Al viden", icon: "🌍" },
                  { id: "conversation" as const, label: "Samtaleanalyse", icon: "💬" },
                ] as { id: SummaryTab; label: string; icon: string }[]).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleSummaryTab(tab.id)}
                    className={`flex-1 py-2 text-xs font-medium transition-colors border-b-2 ${
                      summaryTab === tab.id
                        ? "border-green-600 text-green-700 bg-green-50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    title={tab.label}
                  >
                    <span className="block text-base leading-tight">{tab.icon}</span>
                    <span className="hidden sm:block">{tab.label}</span>
                  </button>
                ))}
              </div>

              <div className="p-4">
                {/* Tab 1: Document summary */}
                {summaryTab === "doc" && (
                  <>
                    {!selectedDoc ? (
                      <p className="text-sm text-gray-400">Vælg et dokument for at se dens AI-sammenfatning.</p>
                    ) : selectedDoc.ai_summary ? (
                      <MdContent content={selectedDoc.ai_summary} />
                    ) : (
                      <p className="text-sm text-gray-400">Ingen sammenfatning tilgængelig for dette dokument.</p>
                    )}
                  </>
                )}

                {/* Tab 2: Al Viden — perspective of selected doc (or global fallback) */}
                {summaryTab === "global" && (
                  <>
                    {selectedDoc ? (
                      // Perspective mode
                      <>
                        <p className="text-xs text-gray-400 mb-3">
                          🔍 Al øvrig viden — set fra perspektivet af{" "}
                          <strong className="text-gray-600">{selectedDoc.doc_title}</strong>
                        </p>
                        {perspectiveLoading && !perspectiveSummaries[selectedDoc.doc_id] ? (
                          <SummaryLoading label="Analyserer øvrig viden fra dette dokuments perspektiv..." />
                        ) : perspectiveSummaries[selectedDoc.doc_id] ? (
                          <MdContent content={perspectiveSummaries[selectedDoc.doc_id]} />
                        ) : (
                          <button
                            onClick={() => loadPerspectiveSummary(selectedDoc)}
                            className="w-full py-3 text-sm text-white rounded-lg font-medium"
                            style={{ backgroundColor: "#2d6a4f" }}
                          >
                            🌍 Generer perspektivoversigt
                          </button>
                        )}
                      </>
                    ) : (
                      // No doc selected — generic global summary
                      <>
                        {globalLoading ? (
                          <SummaryLoading label="Genererer oversigt over al viden..." />
                        ) : globalSummary ? (
                          <MdContent content={globalSummary} />
                        ) : (
                          <button
                            onClick={loadGlobalSummary}
                            className="w-full py-3 text-sm text-white rounded-lg font-medium"
                            style={{ backgroundColor: "#2d6a4f" }}
                          >
                            🌍 Generer oversigt over al viden
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Tab 3: Conversation analysis */}
                {summaryTab === "conversation" && (
                  <>
                    {convLoading ? (
                      <SummaryLoading label="Analyserer din samtale..." />
                    ) : convSummary ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-gray-400">🔐 Samtale-ID er pseudo-anonymiseret</span>
                          <button
                            onClick={loadConvSummary}
                            className="text-xs text-green-700 hover:underline"
                          >
                            Opdater ↺
                          </button>
                        </div>
                        <MdContent content={convSummary} />
                      </>
                    ) : (
                      <button
                        onClick={loadConvSummary}
                        className="w-full py-3 text-sm text-white rounded-lg font-medium"
                        style={{ backgroundColor: "#2d6a4f" }}
                      >
                        💬 Analysér mine samtaler
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Inline selection analysis result */}
            {(analysisLoading || analysisResult) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm flex items-center gap-2">
                  🌿 AI Analyse
                  {analysisLoading && (
                    <span className="flex items-center gap-1.5 text-xs text-green-600 font-normal">
                      <svg className="animate-spin h-3.5 w-3.5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Tænker...
                    </span>
                  )}
                </h3>
                {analysisLoading && !analysisResult ? (
                  <SummaryLoading label="Søger i dokumenterne og formulerer svar..." />
                ) : (
                  <MdContent content={analysisResult} />
                )}
              </div>
            )}

            {selectedDoc && !analysisResult && !analysisLoading && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <h3 className="font-semibold text-green-800 text-sm mb-2">💡 Tip</h3>
                <p className="text-sm text-green-700 leading-relaxed">
                  Markér tekst direkte på PDF-siden og klik på en analyseknap for AI-analyse.
                  Sider uden tekstlag (skannet) viser <strong>📷 Skannet side</strong>.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
